import { useState, KeyboardEvent } from "react";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { useAskAssistant, type AssistantResponse } from "@/lib/api/assistant";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { AssumptionsPanel } from "./components/AssumptionsPanel";
import { WarningsPanel, ErrorsPanel } from "./components/MessagesPanel";
import { UnsupportedPanel } from "./components/UnsupportedPanel";
import { ResultsRenderer } from "./components/ResultsRenderer";

const EXAMPLE_QUERIES: string[] = [
  "average price change for soybeans this week",
  "21 day window avg price change for sell signals between Mar 14 and Mar 25 for main commodities",
  "when was the last sell signal for corn?",
  "list daily corn closes between Mar 14 and Mar 25",
];

export function AssistantDashboard() {
  const { toast } = useToast();
  const [query, setQuery] = useState<string>("");
  const [response, setResponse] = useState<AssistantResponse | null>(null);
  const askMutation = useAskAssistant();

  const submit = async () => {
    const trimmed = query.trim();
    if (trimmed.length === 0) return;
    if (askMutation.isLoading) return;
    try {
      const result = await askMutation.mutateAsync({ query: trimmed, retries: 2 });
      if (result.success) {
        setResponse(result.data);
      } else {
        toast({
          title: "Assistant request failed",
          description: result.message || "Unknown error",
          variant: "destructive",
        });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      toast({
        title: "Assistant request failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Cmd/Ctrl+Enter
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  const isPending = askMutation.isLoading;
  const hasResponse = response != null;
  const hasResults = hasResponse && response.results.length > 0;
  const hasUnsupported = hasResponse && response.unsupported != null;
  const hasNothingToShow =
    hasResponse && !hasUnsupported && !hasResults && response.errors.length === 0;

  return (
    <div className="flex flex-col h-screen max-h-[calc(100vh-4rem)]">
      <div className="mb-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 text-white flex items-center justify-center">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
            Assistant
          </h1>
          <p className="text-sm text-slate-500">
            Ask about HedgeBeacon prices, price signals, and price changes.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 pr-1">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4">
          <Textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask a question — e.g. 'average price change for soybeans this week'"
            rows={3}
            className="resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 text-base"
            disabled={isPending}
          />
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
            <div className="text-[11px] text-slate-500">
              Press <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono">⌘/Ctrl</kbd>
              {" + "}
              <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono">Enter</kbd>
              {" "}to send
            </div>
            <Button
              onClick={submit}
              disabled={isPending || query.trim().length === 0}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Thinking…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Ask
                </>
              )}
            </Button>
          </div>
        </div>

        {!hasResponse && !isPending && (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
              Try one of these
            </div>
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_QUERIES.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setQuery(q)}
                  className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-100 text-slate-700"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {hasResponse && (
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wide text-slate-500 px-1">Question</div>
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800">
              {response.query}
            </div>

            {response.unsupported && (
              <UnsupportedPanel unsupported={response.unsupported} />
            )}

            {response.plan?.interpretation && (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 px-4 py-3">
                <div className="text-[11px] uppercase tracking-wide text-indigo-700 mb-0.5">
                  Interpretation
                </div>
                <div className="text-sm text-slate-800">{response.plan.interpretation}</div>
              </div>
            )}

            <AssumptionsPanel assumptions={response.assumptions} />
            <WarningsPanel warnings={response.warnings} />
            <ErrorsPanel errors={response.errors} />

            {hasResults && (
              <div className="space-y-3 pt-1">
                <div className="text-xs uppercase tracking-wide text-slate-500 px-1">
                  Results
                </div>
                <ResultsRenderer results={response.results} />
              </div>
            )}

            {hasNothingToShow && (
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500 text-center">
                No results to display.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AssistantDashboard;
